import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Mic, RotateCcw, Square } from 'lucide-react';

import { readJson } from '../../portal/portalFetch';
import { connectLabLiveStt } from '../labLiveStt';
import { createLabMic } from '../labMic';
import { keepWordsFromScript, parseKeepList } from '../keepWords';
import { appendTranscript, coversSpoken, normalizeSpoken } from '../labTranscript';
import { PAUSE_MS, stripEndStop, withFullStop } from '../sentencePause';
import { wordErrorRate } from '../wordAccuracy';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

const C = {
  bg: '#0f172a',
  card: '#111827',
  line: '#1e293b',
  text: '#e2e8f0',
  mute: '#94a3b8',
  green: '#86efac',
  amber: '#fbbf24',
};

const SAMPLE_SCRIPTS = [
  {
    id: 'intro',
    label: 'Intro',
    text: 'My name is Rahul Sharma. I have five years of experience in Python and React. I built an API that handles user login and payments.',
  },
  {
    id: 'company',
    label: 'Company',
    text: 'I work at Infosys on the Paytm payments project. We use Java and Spring Boot with PostgreSQL.',
  },
  {
    id: 'project',
    label: 'Project',
    text: 'The last project was BharatPe checkout. I used Node, React, and MongoDB. My manager was Priya.',
  },
  {
    id: 'tech',
    label: 'Technical',
    text: 'I used PostgreSQL with SQLAlchemy. The slow query was a missing index on the foreign key. After adding it, response time dropped from two seconds to eighty milliseconds.',
  },
];

const ENGINE_ERRORS = {
  deepgram_key_missing: 'Deepgram API key is missing. Set DEEPGRAM_API_KEY in backend/.env and restart the API.',
  deepgram_unauthorized: 'Deepgram rejected the API key. Check DEEPGRAM_API_KEY.',
  deepgram_payment_required: 'Deepgram account needs billing or credits.',
  deepgram_empty: 'Deepgram heard no English speech in that clip.',
  deepgram_failed: 'Could not reach Deepgram. Restart the API and try again.',
  deepgram_ssl: 'Server could not verify Deepgram SSL. Restart the API after the latest backend update.',
  too_short: 'Audio clip was too short.',
};

export default function SttLab() {
  const streamRef = useRef(null);
  const micRef = useRef(null);
  const liveRef = useRef(null);
  const wantedRef = useRef(false);
  const finalsRef = useRef('');
  const interimRef = useRef('');
  const pauseTimerRef = useRef(null);

  const [expected, setExpected] = useState(SAMPLE_SCRIPTS[0].text);
  const [keepNames, setKeepNames] = useState(() => keepWordsFromScript(SAMPLE_SCRIPTS[0].text).join(', '));
  const [listening, setListening] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [error, setError] = useState('');
  const [transcript, setTranscript] = useState('');
  const [log, setLog] = useState([]);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState({ deepgram: false, model: 'nova-3', lang: 'en-IN' });

  const polish = useCallback((text) => normalizeSpoken(text), []);

  const addLog = useCallback((row) => {
    setLog((prev) => [{ id: `${Date.now()}-${Math.random()}`, at: new Date().toLocaleTimeString(), ...row }, ...prev].slice(0, 40));
  }, []);

  const publish = useCallback(() => {
    setTranscript(polish(appendTranscript(finalsRef.current, interimRef.current)));
  }, [polish]);

  const clearPauseTimer = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  }, []);

  const closeSentence = useCallback(() => {
    pauseTimerRef.current = null;
    const merged = polish(appendTranscript(finalsRef.current, stripEndStop(interimRef.current)));
    interimRef.current = '';
    if (!merged) return;
    finalsRef.current = withFullStop(merged);
    publish();
  }, [polish, publish]);

  const armPauseStop = useCallback(() => {
    clearPauseTimer();
    pauseTimerRef.current = setTimeout(closeSentence, PAUSE_MS);
  }, [clearPauseTimer, closeSentence]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/stt-test/status`)
      .then((res) => readJson(res))
      .then((data) => {
        if (cancelled) return;
        setStatus({
          deepgram: Boolean(data.deepgram),
          model: data.model || 'nova-3',
          lang: data.lang || 'en-IN',
        });
        if (!data.deepgram) setError(ENGINE_ERRORS.deepgram_key_missing);
      })
      .catch(() => {
        if (!cancelled) setError('Could not reach the STT server. Is the API running on port 9999?');
      });
    return () => { cancelled = true; };
  }, []);

  const handleLiveError = useCallback((code) => {
    const msg = ENGINE_ERRORS[code] || String(code || 'Deepgram live STT failed.');
    setError(msg);
    addLog({ source: 'deepgram', engine: 'error', text: msg });
  }, [addLog]);

  const handleTranscript = useCallback((payload) => {
    const text = stripEndStop(polish(payload.text || ''));
    if (!text || !wantedRef.current) return;
    armPauseStop();
    if (payload.is_final) {
      if (!coversSpoken(finalsRef.current, text)) {
        finalsRef.current = polish(appendTranscript(finalsRef.current, text));
      }
      interimRef.current = '';
      addLog({
        source: 'deepgram',
        engine: payload.model || 'nova-3',
        text,
        confidence: payload.confidence,
      });
    } else {
      interimRef.current = text;
    }
    publish();
  }, [addLog, armPauseStop, polish, publish]);

  const stopMicMeter = useCallback(() => {
    const stream = streamRef.current;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setMicReady(false);
    setMicLevel(0);
  }, []);

  const startMic = useCallback(async () => {
    setError((prev) => (prev === ENGINE_ERRORS.deepgram_key_missing ? prev : ''));
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: { ideal: 1 } },
      video: false,
    });
    streamRef.current = stream;
    setMicReady(true);
    return stream;
  }, []);

  const stopListening = useCallback(() => {
    wantedRef.current = false;
    clearPauseTimer();
    closeSentence();
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    try { micRef.current?.flush(); } catch (_) { /* ignore */ }
    setListening(false);
  }, [clearPauseTimer, closeSentence]);

  const resetAll = useCallback(() => {
    stopListening();
    finalsRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setLog([]);
    setCopied(false);
  }, [stopListening]);

  const startListening = useCallback(async () => {
    try {
      if (!streamRef.current) await startMic();
      wantedRef.current = true;
      setListening(true);
      if (status.deepgram) setError('');
      try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
      liveRef.current = connectLabLiveStt({
        keyterms: [...keepWordsFromScript(expected), ...parseKeepList(keepNames)],
        onReady: (info) => {
          setStatus((prev) => ({
            ...prev,
            deepgram: true,
            model: info.model || prev.model,
            lang: info.lang || prev.lang,
          }));
        },
        onTranscript: handleTranscript,
        onError: handleLiveError,
      });
      await micRef.current?.start?.();
      micRef.current?.ensureRunning?.();
    } catch (_) {
      setError('Microphone permission is required. Allow the mic and try again.');
      wantedRef.current = false;
      setListening(false);
    }
  }, [expected, handleLiveError, handleTranscript, keepNames, startMic, status.deepgram]);

  useEffect(() => {
    const transcriber = createLabMic({
      getStream: () => streamRef.current,
      shouldCapture: () => wantedRef.current,
      onLevel: (level) => setMicLevel(level),
      onPcm: (pcm) => liveRef.current?.sendPcm(pcm),
    });
    micRef.current = transcriber;
    return () => {
      transcriber.stop();
      if (micRef.current === transcriber) micRef.current = null;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (!wantedRef.current) return;
      micRef.current?.ensureRunning?.();
    }, 800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => () => {
    wantedRef.current = false;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    stopMicMeter();
  }, [stopMicMeter]);

  const stats = wordErrorRate(expected, transcript);
  const card = {
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: 14,
    padding: 16,
  };

  const copyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(transcript || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 20px 60px' }}>
        <div style={{ fontSize: 12, color: C.mute, letterSpacing: 0.7, textTransform: 'uppercase' }}>Recruiter24x7 · STT Lab</div>
        <h1 style={{ margin: '6px 0 8px', fontSize: 28 }}>Deepgram Nova-3 STT</h1>
        <p style={{ color: C.mute, marginTop: 0, lineHeight: 1.6, maxWidth: 780 }}>
          Live stream: mic → our server → Deepgram Nova-3 (`en-IN`). API key server par rehti hai.
        </p>
        <div style={{ fontSize: 13, marginBottom: 16, color: status.deepgram ? C.green : '#fca5a5' }}>
          {status.deepgram
            ? `Deepgram ready · ${status.model} · ${status.lang || 'en-IN'} · live`
            : 'Deepgram not configured — add DEEPGRAM_API_KEY in backend/.env and restart the API.'}
        </div>

        {error && (
          <div style={{ ...card, borderColor: 'rgba(248,113,113,0.4)', color: '#fca5a5', marginBottom: 16 }}>{error}</div>
        )}

        <div className="r-split-main" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(280px, 0.9fr)', gap: 16 }}>
          <section style={card}>
            <div style={{ fontSize: 12, color: C.mute, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>English script to read</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              {SAMPLE_SCRIPTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setExpected(item.text);
                    setKeepNames(keepWordsFromScript(item.text).join(', '));
                  }}
                  style={{
                    border: `1px solid ${C.line}`, background: expected === item.text ? '#1e3a5f' : '#0f172a',
                    color: C.text, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <textarea
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              rows={5}
              style={{
                width: '100%', boxSizing: 'border-box', background: '#0f172a', color: C.text,
                border: `1px solid ${C.line}`, borderRadius: 10, padding: 12, fontSize: 14, lineHeight: 1.6,
              }}
            />
            <div style={{ fontSize: 12, color: C.mute, textTransform: 'uppercase', letterSpacing: 0.6, margin: '14px 0 8px' }}>
              Names, companies, projects (keep as spoken)
            </div>
            <input
              value={keepNames}
              onChange={(e) => setKeepNames(e.target.value)}
              placeholder="Devendra Kumar, Amartam, BharatPe"
              style={{
                width: '100%', boxSizing: 'border-box', background: '#0f172a', color: C.text,
                border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px', fontSize: 14,
              }}
            />
            <div style={{ fontSize: 12, color: C.mute, marginTop: 6, lineHeight: 1.5 }}>
              Hindi/Indian names English dictionary mein nahi hote. Yahan spelling likho — Deepgram unhe spoken form mein lock karega, Devanagari nahi.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {!listening ? (
                <button
                  type="button"
                  onClick={startListening}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', borderRadius: 10,
                    padding: '10px 16px', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  <Mic size={16} /> Start listening
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopListening}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', borderRadius: 10,
                    padding: '10px 16px', background: '#b91c1c', color: '#fff', fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  <Square size={16} /> Stop
                </button>
              )}
              <button
                type="button"
                onClick={resetAll}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${C.line}`,
                  borderRadius: 10, padding: '10px 16px', background: '#0f172a', color: C.text, cursor: 'pointer',
                }}
              >
                <RotateCcw size={16} /> Reset
              </button>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 11, color: C.mute, marginBottom: 4 }}>
                  Mic {micReady ? 'ready' : 'off'} · {listening ? 'listening' : 'idle'} · Nova-3 live
                </div>
                <div style={{ height: 8, background: '#1e293b', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${micLevel}%`, height: '100%', background: listening ? '#22c55e' : '#64748b' }} />
                </div>
              </div>
            </div>
          </section>

          <aside style={card}>
            <div style={{ fontSize: 12, color: C.mute, textTransform: 'uppercase', letterSpacing: 0.6 }}>Accuracy vs script</div>
            <div style={{ fontSize: 42, fontWeight: 800, margin: '8px 0 4px', color: stats.accuracy >= 80 ? C.green : stats.accuracy >= 50 ? C.amber : '#fca5a5' }}>
              {expected.trim() && transcript.trim() ? `${stats.accuracy}%` : '—'}
            </div>
            <div style={{ color: C.mute, fontSize: 13, lineHeight: 1.7 }}>
              Word error rate {expected.trim() && transcript.trim() ? stats.wer.toFixed(2) : '—'}<br />
              Substitutions {stats.substitutions} · Insertions {stats.insertions} · Deletions {stats.deletions}<br />
              Expected {stats.refWords} words · Heard {stats.hypWords} words
            </div>
          </aside>
        </div>

        <section style={{ ...card, marginTop: 16, minHeight: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Deepgram transcript</div>
              <div style={{ fontSize: 11, color: C.mute }}>Live Nova-3 · {status.lang || 'en-IN'} · {status.model}</div>
            </div>
            <button type="button" onClick={copyTranscript} style={{ border: 'none', background: 'transparent', color: C.mute, cursor: 'pointer' }}>
              <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 16, lineHeight: 1.7, color: transcript ? C.text : '#64748b', whiteSpace: 'pre-wrap' }}>
            {transcript || '—'}
          </div>
        </section>

        <section style={{ ...card, marginTop: 16 }}>
          <div style={{ fontSize: 12, color: C.mute, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>Chunk log</div>
          {!log.length ? (
            <div style={{ color: C.mute, fontSize: 13 }}>Start listening and speak. Transcript streams live from Deepgram.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflow: 'auto' }}>
              {log.map((row) => (
                <div key={row.id} style={{ fontSize: 13, lineHeight: 1.5, borderBottom: `1px solid ${C.line}`, paddingBottom: 8 }}>
                  <span style={{ color: C.mute, fontFamily: 'ui-monospace, Menlo, monospace' }}>{row.at}</span>
                  {' · '}
                  <strong>{row.source}</strong>
                  {row.engine ? ` · ${row.engine}` : ''}
                  {row.ms != null ? ` · ${row.ms}ms` : ''}
                  {row.confidence != null ? ` · ${Number(row.confidence).toFixed(2)}` : ''}
                  <div style={{ color: C.text, marginTop: 2 }}>{row.text}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
